import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import Login from './components/layout/Login';
import Dashboard from './components/dashboard/Dashboard';
import ProductList from './components/products/ProductList';
import ProductForm from './components/products/ProductForm';
import InventoryList from './components/inventory/InventoryList';
import NearExpiry from './components/inventory/NearExpiry';
import LowStock from './components/inventory/LowStock';
import StockMovements from './components/inventory/StockMovements';
import BarcodeLookup from './components/inventory/BarcodeLookup';
import InvoiceList from './components/billing/InvoiceList';
import InvoiceForm from './components/billing/InvoiceForm';
import InvoiceView from './components/billing/InvoiceView';
import PurchaseOrderList from './components/purchases/PurchaseOrderList';
import PurchaseOrderForm from './components/purchases/PurchaseOrderForm';
import GRNList from './components/purchases/GRNList';
import GRNForm from './components/purchases/GRNForm';
import SupplierList from './components/suppliers/SupplierList';
import CustomerList from './components/customers/CustomerList';
import GSTReports from './components/reports/GSTReports';
import SalesReport from './components/reports/SalesReport';
import StockReport from './components/reports/StockReport';
import EwayBillList from './components/billing/EwayBillList';

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        {/* Products */}
        <Route path="products" element={<ProductList />} />
        <Route path="products/new" element={<ProtectedRoute roles={['admin','inventory_manager']}><ProductForm /></ProtectedRoute>} />
        <Route path="products/:id/edit" element={<ProtectedRoute roles={['admin','inventory_manager']}><ProductForm /></ProtectedRoute>} />
        {/* Inventory */}
        <Route path="inventory" element={<InventoryList />} />
        <Route path="inventory/near-expiry" element={<NearExpiry />} />
        <Route path="inventory/low-stock" element={<LowStock />} />
        <Route path="inventory/movements" element={<StockMovements />} />
        <Route path="inventory/scan" element={<BarcodeLookup />} />
        {/* Billing */}
        <Route path="invoices" element={<InvoiceList />} />
        <Route path="invoices/new" element={<ProtectedRoute roles={['admin','billing_staff']}><InvoiceForm /></ProtectedRoute>} />
        <Route path="invoices/:id" element={<InvoiceView />} />
        {/* E-way Bills */}
        <Route path="eway-bills" element={<EwayBillList />} />
        {/* Purchases */}
        <Route path="purchases/orders" element={<PurchaseOrderList />} />
        <Route path="purchases/orders/new" element={<ProtectedRoute roles={['admin','inventory_manager']}><PurchaseOrderForm /></ProtectedRoute>} />
        <Route path="purchases/grn" element={<GRNList />} />
        <Route path="purchases/grn/new" element={<ProtectedRoute roles={['admin','inventory_manager']}><GRNForm /></ProtectedRoute>} />
        {/* Masters */}
        <Route path="suppliers" element={<SupplierList />} />
        <Route path="customers" element={<CustomerList />} />
        {/* Reports */}
        <Route path="reports/gst" element={<GSTReports />} />
        <Route path="reports/sales" element={<SalesReport />} />
        <Route path="reports/stock" element={<StockReport />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
